import { useState } from 'react'
import { ArrowRight, X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Combobox, type ComboboxOption } from '@/components/ui/Combobox'
import { usePresalesDashboardStore } from '../store'

interface ReassignProspectDialogProps {
  prospectCompany: string
  onReassign: (dealId: number) => Promise<void>
  onCancel: () => void
}

export function ReassignProspectDialog({ prospectCompany: _prospectCompany, onReassign, onCancel }: ReassignProspectDialogProps) {
  const { deals } = usePresalesDashboardStore()
  const [selectedDealId, setSelectedDealId] = useState<number | ''>('')
  const [isSaving, setIsSaving] = useState(false)

  const dealOptions: ComboboxOption[] = (deals || [])
    .filter((d) => !d.is_closed || d.closed_won)
    .map((d) => ({
      value: String(d.id),
      label: `${d.deal_name}${d.company_name ? ` (${d.company_name})` : ''}`,
    }))

  const handleReassign = async () => {
    if (!selectedDealId) return
    setIsSaving(true)
    try {
      await onReassign(Number(selectedDealId))
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <div className="flex items-center gap-2 py-1.5 px-2 bg-[var(--bg-secondary)] rounded-md border border-[var(--border-color)]">
      <ArrowRight className="w-3 h-3 text-[var(--text-muted)] flex-shrink-0" />
      <span className="text-xs text-[var(--text-muted)] whitespace-nowrap">Reassign to:</span>
      <div className="flex-1 min-w-[160px]">
        <Combobox
          options={dealOptions}
          value={selectedDealId ? String(selectedDealId) : ''}
          onChange={(val) => setSelectedDealId(val ? Number(val) : '')}
          placeholder="Search deals..."
          inputClassName="!py-1 !text-xs"
        />
      </div>
      <Button
        size="sm"
        onClick={handleReassign}
        disabled={!selectedDealId || isSaving}
        className="!py-1 !px-2 !text-xs"
      >
        {isSaving ? '...' : 'Move'}
      </Button>
      <button
        onClick={onCancel}
        className="p-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  )
}
